// src/utils/fetchWrapper.js - Fetch wrapper with retries and error handling

// Import unified notification system
import { showNotification } from '../notifications/notifications';
import analytics from '../analytics/analytics';

// Show toast notification (now uses unified notification system)
function showToast(message, type = 'error', duration = 3000) {
  // Map old types to new notification system
  const typeMap = {
    'success': 'success',
    'error': 'error',
    'warning': 'warning',
    'info': 'info'
  };

  showNotification(message, {
    type: typeMap[type] || 'info',
    duration,
    dismissible: true
  });
}

// Retry configuration
const DEFAULT_RETRY_CONFIG = {
  retries: 3,
  retryDelay: 1000, // Start with 1 second
  retryDelayMultiplier: 2, // Exponential backoff
  retryCondition: (error) => {
    // Retry on network errors or 5xx server errors
    return !error.response || (error.response.status >= 500 && error.response.status <= 599);
  }
};

// Enhanced fetch with retries and error handling
export async function fetchWithRetry(url, options = {}, config = {}) {
  const {
    retries = DEFAULT_RETRY_CONFIG.retries,
    retryDelay = DEFAULT_RETRY_CONFIG.retryDelay,
    retryDelayMultiplier = DEFAULT_RETRY_CONFIG.retryDelayMultiplier,
    retryCondition = DEFAULT_RETRY_CONFIG.retryCondition,
    timeout = 30000,
    showToastOnError = true,
    toastMessage = null
  } = { ...DEFAULT_RETRY_CONFIG, ...config };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  let lastError;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.response = response;
        error.status = response.status;
        
        // Try to get error details from response
        try {
          const errorData = await response.json();
          error.message = errorData.error || errorData.message || error.message;
        } catch {
          // Response is not JSON, keep original message
        }
        
        throw error;
      }
      
      return response;
    } catch (error) {
      lastError = error;
      clearTimeout(timeoutId);
      
      // Don't retry if it's the last attempt or retry condition is not met
      if (attempt === retries || !retryCondition(error)) {
        break;
      }
      
      // Wait before retry with exponential backoff
      const delay = retryDelay * Math.pow(retryDelayMultiplier, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // Show toast error if enabled
  if (showToastOnError) {
    const errorMessage = toastMessage || getErrorMessage(lastError);
    showToast(errorMessage, 'error');
  }
  
  throw lastError;
}

// Get user-friendly error message
function getErrorMessage(error) {
  if (error.name === 'AbortError') {
    return 'Request timed out. Please try again.';
  }
  
  if (error.message.includes('fetch') || error.message.includes('network')) {
    return 'Network error. Please check your connection.';
  }
  
  if (error.status >= 500) {
    return 'Server error. Please try again later.';
  }
  
  if (error.status === 404) {
    return 'Resource not found.';
  }
  
  if (error.status === 403) {
    return 'Access denied.';
  }
  
  if (error.status === 401) {
    return 'Authentication required. Please log in.';
  }
  
  return error.message || 'Something went wrong. Please try again.';
}

// Convenience methods for common HTTP methods
export const api = {
  get: (url, config = {}) => 
    fetchWithRetry(url, { method: 'GET' }, config),
    
  post: (url, data, config = {}) => 
    fetchWithRetry(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...config.headers },
      body: JSON.stringify(data)
    }, config),
    
  put: (url, data, config = {}) => 
    fetchWithRetry(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...config.headers },
      body: JSON.stringify(data)
    }, config),
    
  delete: (url, config = {}) => 
    fetchWithRetry(url, { method: 'DELETE' }, config)
};

// Export toast function for manual use
export { showToast };

// ─── API Call Tracking ───────────────────────────────────────────────────────

/**
 * Extract a readable endpoint label from a URL.
 * "https://getinsights-wtylm37ajq-uc.a.run.app/api/foo?bar=1" → "getinsights /api/foo"
 */
function getEndpointLabel(url) {
  try {
    const { hostname, pathname } = new URL(url);
    const service = hostname.split('-')[0]; // e.g. "getinsights" from Cloud Run hostname
    const path = pathname === '/' ? '' : pathname;
    return `${service}${path}`;
  } catch {
    return 'unknown';
  }
}

// Only intercept calls to our own API endpoints — skip Firebase, analytics, CDN, blob URLs
const API_HOSTNAMES = ['.run.app'];
function isApiCall(url) {
  return typeof url === 'string' && API_HOSTNAMES.some(p => url.includes(p));
}

/**
 * Install a global fetch interceptor that tracks all Cloud Function API calls.
 * Call once from main.jsx during app initialization.
 */
export function installFetchTracker() {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async function trackedFetch(url, options = {}) {
    if (!isApiCall(url)) return originalFetch(url, options);

    const endpoint = getEndpointLabel(url);
    const method = (options.method || 'GET').toUpperCase();
    const startTime = Date.now();

    try {
      const response = await originalFetch(url, options);
      analytics.trackEvent('api_call_completed', {
        endpoint,
        method,
        status: response.status,
        duration_ms: Date.now() - startTime,
        success: response.ok,
      });
      return response;
    } catch (error) {
      analytics.trackEvent('api_call_failed', {
        endpoint,
        method,
        error: error.name,
        duration_ms: Date.now() - startTime,
      });
      throw error;
    }
  };
}